import { FC, ReactNode } from "react";
import { Box, Button, Paper, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

interface InventoryEmptyStateProps {
  /** Icon element rendered inside the circular badge (e.g. <InventoryIcon />). */
  icon: ReactNode;
  title: string;
  description: string;
  /** Optional call-to-action button. Omit to render a message-only empty state. */
  actionLabel?: string;
  onAction?: () => void;
  /** Optional small helper line under the button. */
  caption?: string;
  /** Height of the empty-state panel. Matches the grid height it replaces. */
  height?: string;
}

// Shared empty-state panel for every Inventory listing tab. Mirrors the
// "No Sales Yet" / "No Inventory Yet" styling used elsewhere in the app so the
// look stays consistent across modules.
const InventoryEmptyState: FC<InventoryEmptyStateProps> = ({
  icon,
  title,
  description,
  actionLabel,
  onAction,
  caption,
  height = "70vh",
}) => (
  <Paper
    elevation={0}
    sx={{
      height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#fafafb",
      border: "2px dashed #e0e0e0",
      borderRadius: 2,
      p: 4,
    }}
  >
    <Box sx={{ textAlign: "center", maxWidth: 400 }}>
      <Box
        sx={{
          width: 120,
          height: 120,
          borderRadius: "50%",
          backgroundColor: "primary.light",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto",
          mb: 3,
          "& .MuiSvgIcon-root": { fontSize: 60, color: "primary.main" },
        }}
      >
        {icon}
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 600, color: "#333", mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: "#666", mb: actionLabel ? 4 : 0, lineHeight: 1.6 }}>
        {description}
      </Typography>
      {actionLabel && onAction && (
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={onAction}
          sx={{
            px: 4,
            py: 2,
            borderRadius: 3,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 600,
            letterSpacing: "0.5px",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-4px) scale(1.02)",
              boxShadow: (theme: any) => `0 8px 25px -8px ${theme.palette.primary.main}`,
            },
            "&:active": {
              transform: "translateY(-2px) scale(0.98)",
            },
          }}
        >
          {actionLabel}
        </Button>
      )}
      {caption && (
        <Typography variant="caption" sx={{ display: "block", mt: 3, color: "#999" }}>
          {caption}
        </Typography>
      )}
    </Box>
  </Paper>
);

export default InventoryEmptyState;
